import { useMutation, useQueryClient } from "@tanstack/react-query";
import { tutorApiService } from "../../../services/tutorApi";
import { Mode } from "../../../types";

/**
 * useStorageMutations
 *
 * React Query Mutations for Storage operations
 *
 * 기능:
 * - 보관함에 추가 (addToStorage)
 * - 보관함에서 삭제 (deleteFromStorage)
 *
 * 대체:
 * - useStorageFeature.addToStorage
 * - useStorageFeature.deleteFromStorage
 */

interface AddToStorageOptions {
  groupId: string;
  title: string;
  isCompleted?: boolean; // 소크라테스용: 면접 완료 여부
}

interface AddToStorageResult {
  success: boolean;
  message?: string;
}

const TUTOR_URLS = {
  EDIT_STORAGE: process.env.REACT_APP_TUTOR_EDIT_STORAGE_LIST,
};

const SOCRATES_URLS = {
  EDIT_STORAGE: process.env.REACT_APP_SOCRATES_EDIT_STORAGE_LIST,
};

/**
 * 보관함에 추가 Mutation
 */
export const useAddToStorageMutation = (mode: Mode) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      options: AddToStorageOptions
    ): Promise<AddToStorageResult> => {
      const { groupId, title, isCompleted = true } = options;

      // 소크라테스는 면접 완료 체크
      if (mode === Mode.SOCRATES && !isCompleted) {
        return {
          success: false,
          message: "면접을 완료한 후 저장할 수 있습니다.",
        };
      }

      const url =
        mode === Mode.SOCRATES
          ? SOCRATES_URLS.EDIT_STORAGE
          : TUTOR_URLS.EDIT_STORAGE;

      const reqData =
        mode === Mode.TUTOR
          ? {
              // 튜터용
              group_id: Number(groupId),
              title: title || "보관된 대화",
            }
          : {
              // 소크라테스용
              group_id: Number(groupId),
              title: title || "보관된 대화",
            };

      const storageData = await tutorApiService.touchAPI("POST", url, reqData);

      if (!storageData) {
        throw new Error("Failed to add to storage");
      }

      return {
        success: true,
        message: "이력이 보관되었습니다.",
      };
    },
    onSuccess: () => {
      // 보관함 목록 캐시 무효화 (자동 refetch)
      queryClient.invalidateQueries({ queryKey: ["storage", mode] });
    },
    onError: (error) => {
      console.error("Failed to add to storage:", error);
    },
  });
};

/**
 * 보관함에서 삭제 Mutation
 */
export const useDeleteFromStorageMutation = (mode: Mode) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (storageId: string): Promise<boolean> => {
      const url =
        mode === Mode.SOCRATES
          ? SOCRATES_URLS.EDIT_STORAGE
          : TUTOR_URLS.EDIT_STORAGE;

      const result = await tutorApiService.touchAPI("DELETE", url, `/${storageId}`);

      if (!result) {
        throw new Error("Failed to delete from storage");
      }

      return true;
    },
    onSuccess: () => {
      // 보관함 목록 캐시 무효화 (자동 refetch)
      queryClient.invalidateQueries({ queryKey: ["storage", mode] });
    },
    onError: (error) => {
      console.error("Failed to delete from storage:", error);
    },
  });
};
